import clsx from "clsx";
import PropTypes from "prop-types";
import * as React from "react";
import Menu from "../../popover/Menu/Menu";
import { colorOf, sizeOf } from "../../utils/propTypes";
import css from "./SplitButton.module.css";

/**
 * SplitButton — Primary action + dropdown menu in a shared container.
 *
 * The leading segment fires the default action (`onClick`). The trailing
 * chevron opens a dropdown menu populated by `children` (typically
 * `<Menu.Item>` elements).
 */
const SplitButton = React.forwardRef(
  (
    {
      variant = "filled",
      size = "md",
      color,
      disabled = false,
      label,
      leading,
      onClick,
      className,
      children,
      ...rest
    },
    ref,
  ) => {
    const classes = clsx(
      css.root,
      css[variant],
      css[size],
      color === "success" && css.success,
      className,
    );

    return (
      <Menu.Root>
        <div ref={ref} role="group" className={classes} {...rest}>
          <button
            type="button"
            className={css.action}
            disabled={disabled}
            onClick={onClick}
          >
            {leading && <span className={css.icon}>{leading}</span>}
            {label}
          </button>
          <span className={css.divider} aria-hidden="true" />
          <Menu.Trigger
            render={
              <button
                type="button"
                className={css.toggle}
                disabled={disabled}
                aria-label="More options"
              />
            }
          >
            <svg
              className={css.chevron}
              viewBox="0 0 10 6"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M1 1L5 5L9 1"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Menu.Trigger>
        </div>
        <Menu.Popup side="bottom" align="end">
          {children}
        </Menu.Popup>
      </Menu.Root>
    );
  },
);

SplitButton.displayName = "SplitButton";

SplitButton.propTypes = {
  variant: PropTypes.oneOf(["filled", "tonal", "outlined", "elevated"]),
  size: sizeOf("sm", "md", "lg"),
  color: colorOf("success"),
  disabled: PropTypes.bool,
  label: PropTypes.string,
  leading: PropTypes.node,
  onClick: PropTypes.func,
  className: PropTypes.string,
  children: PropTypes.node,
};

export default SplitButton;
